import { styled } from "goober";
import { useCallback, useRef } from "react";
import { playClick, playRelease } from "../lib/sounds";

const MIN_HOLD_FOR_RELEASE_SOUND_MS = 140;

const StyledClickButton = styled("button")`
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

interface Props extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onClick" | "onPointerDown"
> {
  onClick?: (() => void) | React.MouseEventHandler<HTMLButtonElement>;
  children: React.ReactNode;
}

export function ClickButton({ onClick, children, ...props }: Props) {
  const pressedAt = useRef<number | null>(null);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (event.currentTarget.disabled) return;
      pressedAt.current = performance.now();
      playClick();
    },
    []
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const heldFor =
        pressedAt.current === null ? 0 : performance.now() - pressedAt.current;
      pressedAt.current = null;
      if (onClick && heldFor >= MIN_HOLD_FOR_RELEASE_SOUND_MS) playRelease();
      if (typeof onClick === "function") onClick(event);
    },
    [onClick]
  );

  return (
    <StyledClickButton
      type="button"
      {...props}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
    >
      {children}
    </StyledClickButton>
  );
}
